import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import EditDocumentModal from '../components/EditDocumentModal';
import PurchaseCreditsModal from '../components/PurchaseCreditsModal';
import html2pdf from 'html2pdf.js'; 

const DashboardPage = () => {
  const { user, userCredits, refreshCredits } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState('all');
  const [documentTypes, setDocumentTypes] = useState({});
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [remainingSubscriptionCredits, setRemainingSubscriptionCredits] = useState(0);
  const [loadingCredits, setLoadingCredits] = useState(true);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPurchaseOption, setSelectedPurchaseOption] = useState({
    type: 'credits', // 'credits' or 'subscription'
    id: null, 
    billingCycle: 'monthly'
  });

  useEffect(() => {
    // Refresh credits when dashboard mounts
    refreshCredits();
    
    const fetchUserDocuments = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('generated_documents')
          .select('*, document_type')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        setDocuments(data || []);

        // Fetch document type details for all documents
        const docTypeIds = [...new Set(data.map(doc => doc.document_type).filter(Boolean))];
        
        if (docTypeIds.length > 0) {
          const { data: typesData, error: typesError } = await supabase
            .from('document_types')
            .select('id, title, category')
            .in('id', docTypeIds);
            
          if (typesError) throw typesError;
          
          // Convert to a lookup object for easier access
          const typesObj = {};
          typesData.forEach(type => {
            typesObj[type.id] = type;
          });
          
          setDocumentTypes(typesObj);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
        setError('Failed to load your documents. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchUserDocuments();
    }
  }, [user, refreshCredits]);

  useEffect(() => {
    const fetchCreditsData = async () => {
      if (!user) return;
      
      try {
        setLoadingCredits(true);
        
        // Fetch subscription info if exists
        const { data: subData, error: subError } = await supabase
          .from('user_subscriptions')
          .select(`
            subscription_id,
            start_date,
            end_date,
            is_yearly,
            subscriptions (
              id,
              tier_name,
              usage_cap,
              price_monthly,
              price_yearly
            )
          `)
          .eq('user_id', user.id)
          .is('end_date', null) // Only active subscriptions
          .single();
          
        if (subError && subError.code !== 'PGRST116') {
          console.error('Error fetching subscription data:', subError);
        }
        
        // If they have a subscription, fetch how many documents they've used this period
        let usageCount = 0;
        if (subData) {
          const startOfPeriod = new Date();
          // Reset to beginning of month
          startOfPeriod.setDate(1);
          startOfPeriod.setHours(0, 0, 0, 0);
          
          const { count, error: usageError } = await supabase
            .from('generated_documents')
            .select('id', { count: 'exact' })
            .eq('user_id', user.id)
            .eq('usage_type', 'subscription')
            .gte('created_at', startOfPeriod.toISOString());
            
          if (usageError) {
            console.error('Error fetching usage count:', usageError);
          } else {
            usageCount = count || 0;
          }
        }
        
        // Update state with fetched data
        setSubscriptionData(subData);
        
        // Calculate remaining subscription credits
        if (subData?.subscriptions) {
          setRemainingSubscriptionCredits(Math.max(0, subData.subscriptions.usage_cap - usageCount));
        }
        
      } catch (error) {
        console.error('Error in fetchCreditsData:', error);
      } finally {
        setLoadingCredits(false);
      }
    };

    fetchCreditsData();
  }, [user]);

  const handleDownloadDocument = async (docId, docTitle) => {
    try {
      setError(null);

      // Call the edge function to get the signed URL
      const { data, error } = await supabase.functions.invoke("get-signed-url", {
        method: "POST",
        body: { documentId: docId },
      });

      if (error) {
        console.error("Error invoking edge function:", error);
        setError("Failed to fetch document link. Please try again.");
        return;
      }

      if (!data?.signedUrl) {
        setError("Could not generate document link.");
        return;
      }

      // Trigger PDF download instead of opening in a new tab
      console.log("Downloading document from URL:", data.signedUrl);
      const a = document.createElement('a');
      a.href = data.signedUrl;
      a.download = `${docTitle || 'document'}.pdf`; // Set the filename
      document.body.appendChild(a); // Append to DOM
      a.click(); // Trigger the download
      document.body.removeChild(a); // Clean up by removing the element
      
    } catch (error) {
      console.error("Error accessing document:", error);
      setError("Failed to open document. Please try again later.");
    }
  };

  const handleEditDocument = (docId) => {
    setSelectedDocumentId(docId);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedDocumentId(null);
  };

  const handleSaveDocument = (updatedDocument) => {
    // Update the document in the local state
    setDocuments(prev => 
      prev.map(doc => 
        doc.id === updatedDocument.id ? updatedDocument : doc
      )
    );
  };

  // Get unique document types for filter options
  const getDocumentTypeOptions = () => {
    const types = ['all'];
    documents.forEach(doc => {
      if (doc.document_type && !types.includes(doc.document_type)) {
        types.push(doc.document_type);
      }
    });
    return types;
  };

  // Filter documents based on selected type
  const filteredDocuments = selectedDocType === 'all' 
    ? documents 
    : documents.filter(doc => doc.document_type === selectedDocType);

  // Get display name for document type
  const getDocumentTypeName = (typeId) => {
    return documentTypes[typeId]?.title || typeId;
  };

  // Get document type category (if available)
  const getDocumentTypeCategory = (typeId) => {
    return documentTypes[typeId]?.category;
  };

  const handleOpenPurchaseModal = (type = 'credits') => {
    setSelectedPurchaseOption({
      type,
      id: type === 'subscription' ? null : null,
      billingCycle: 'monthly'
    });
    setShowPurchaseModal(true);
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">My Dashboard</h1>
          <Link 
            to="/createdocument" 
            className="px-5 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full text-sm font-medium hover:shadow-lg hover:shadow-indigo-500/30 transition-all hover:scale-105"
          >
            Create New Document
          </Link>
        </div>
        
        {error && (
          <div className="mb-8 p-4 bg-red-50 text-red-500 rounded-lg">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">My Documents</h2>
            
            {/* Document type filter - moved to be under "My Documents" heading */}
            {!loading && documents.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2 w-full justify-start">
                {getDocumentTypeOptions().map(type => (
                  <button
                    key={type}
                    onClick={() => setSelectedDocType(type)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedDocType === type
                        ? 'bg-primary text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    {type === 'all' ? 'All Documents' : getDocumentTypeName(type)}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {loading ? (
            <div className="p-12 flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : filteredDocuments.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {filteredDocuments.map((doc) => (
                <div key={doc.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center mb-1">
                        <h3 className="text-lg font-medium text-gray-800">{doc.title}</h3>
                        {doc.document_type && (
                          <span className="ml-3 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                            {getDocumentTypeName(doc.document_type)}
                          </span>
                        )}
                        {getDocumentTypeCategory(doc.document_type) && (
                          <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                            {getDocumentTypeCategory(doc.document_type)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Created: {new Date(doc.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex space-x-3">
                      <Link 
                        to={`/document/${doc.id}`}
                        className="px-4 py-2 text-sm text-primary border border-primary rounded-md hover:bg-primary/5 transition-colors"
                      >
                        View
                      </Link>
                      <button 
                        onClick={() => handleDownloadDocument(doc.id, doc.title)}
                        className="px-4 py-2 text-sm text-blue-700 border border-blue-700 rounded-md hover:bg-blue-50 transition-colors"
                      >
                        Download
                      </button>
                      <button 
                        onClick={() => handleEditDocument(doc.id)}
                        className="px-4 py-2 text-sm text-white bg-primary rounded-md hover:bg-primary-dark transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <p className="text-gray-500 mb-4">
                {documents.length > 0 
                  ? `No documents found in the "${selectedDocType === 'all' ? 'All' : getDocumentTypeName(selectedDocType)}" category.` 
                  : "You haven't created any documents yet."}
              </p>
              {documents.length > 0 && selectedDocType !== 'all' ? (
                <button 
                  onClick={() => setSelectedDocType('all')}
                  className="inline-flex items-center text-primary hover:text-primary-dark"
                >
                  View all documents
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              ) : (
                <Link 
                  to="/createdocument" 
                  className="inline-flex items-center text-primary hover:text-primary-dark"
                >
                  Browse documents to get started
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Stats Box */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">Credits & Subscription</h2>
            <Link 
              to="/pricing" 
              className="text-sm text-primary hover:text-primary-dark font-medium flex items-center"
            >
              Get more credits
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Documents Created */}
            <div className="flex p-4 bg-blue-50 rounded-lg h-full">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4 self-start">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="flex flex-col">
                <h3 className="text-lg font-medium text-gray-800">Documents Created</h3>
                <p className="text-2xl font-bold text-gray-900 mt-2">{documents.length}</p>
                <div className="mt-auto pt-4 invisible">
                  {/* This invisible element helps maintain consistent height */}
                  <span className="text-xs text-gray-500">&nbsp;</span>
                </div>
              </div>
            </div>

            {/* Subscription Credits */}
            <div className="flex p-4 bg-indigo-50 rounded-lg h-full">
              <div className="p-3 rounded-full bg-indigo-100 text-indigo-600 mr-4 self-start">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9a2 2 0 10-4 0v5a2 2 0 01-2 2h6m-6-4h4m8 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex flex-col">
                <h3 className="text-lg font-medium text-gray-800">Subscription Credits</h3>
                {subscriptionData ? (
                  <>
                    <div className="mt-2">
                      <p className="text-2xl font-bold text-gray-900">
                        {remainingSubscriptionCredits} 
                        <span className="text-sm font-normal text-gray-500 ml-1">
                          of {subscriptionData.subscriptions.usage_cap} monthly
                        </span>
                      </p>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2 mb-1">
                        <div 
                          className="bg-indigo-600 h-1.5 rounded-full"
                          style={{ width: `${Math.min(100, (remainingSubscriptionCredits / subscriptionData.subscriptions.usage_cap) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Resets on {new Date(subscriptionData.start_date).getDate()}/{new Date(subscriptionData.start_date).getMonth() + 1}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-gray-600 mt-2">No active subscription</p>
                    <div className="mt-auto pt-1">
                      <button 
                        onClick={() => handleOpenPurchaseModal('subscription')}
                        className="text-sm font-medium text-blue-500 hover:text-blue-600 inline-flex items-center"
                      >
                        Get a plan
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Individual Credits */}
            <div className="flex p-4 bg-green-50 rounded-lg h-full">
              <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4 self-start">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex flex-col">
                <h3 className="text-lg font-medium text-gray-800">Individual Credits</h3>
                <p className="text-2xl font-bold text-gray-900 mt-2">{userCredits}</p>
                <div className="mt-auto pt-1 flex flex-col">
                  <p className="text-xs text-gray-500">
                    Never expire
                  </p>
                  <button 
                    onClick={() => handleOpenPurchaseModal('credits')}
                    className="text-sm font-medium text-blue-500 hover:text-blue-600 inline-flex items-center mt-1"
                  >
                    Purchase more
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Document Modal */}
      {isEditModalOpen && (
        <EditDocumentModal
          documentId={selectedDocumentId}
          isOpen={isEditModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveDocument}
        />
      )}

      {/* Purchase Credits Modal */}
      <PurchaseCreditsModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        initialOption={selectedPurchaseOption.type}
        selectedCredits={null}
        selectedSubscription={selectedPurchaseOption.id}
        initialBillingCycle={selectedPurchaseOption.billingCycle}
      />
    </div>
  );
};

export default DashboardPage;